import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, X, Settings } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrganization } from "@/hooks/useOrganization";
import { orgScopedKey } from "@/lib/queryKeys";
import { useLanguage } from "@/hooks/useLanguage";
import { tokenHealth } from "@/lib/fio/tokenHealth";
import { fetchTokenAccounts } from "@/lib/fio/tokenAccounts";
import { FioTokenHelp } from "@/components/settings/FioTokenAlert";

export function SyncErrorBanner() {
  const [dismissed, setDismissed] = useState(false);
  const { data: user } = useCurrentUser();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();

  const { data: accounts } = useQuery({
    queryKey: orgScopedKey("bank-accounts-sync-error", orgId ?? ""),
    enabled: !!user?.id && !!orgId,
    // Stay fresh so the banner hides soon after sync_error is cleared in the DB
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: () => fetchTokenAccounts(orgId!),
  });

  if (dismissed || !accounts?.length) return null;

  const now = new Date();
  const refresh = accounts.filter((a) => tokenHealth(a, now) === "refresh_required");
  const inactive = accounts.filter((a) => tokenHealth(a, now) === "inactive");
  const suspected = accounts.filter((a) => tokenHealth(a, now) === "inactive_suspected");
  const expiring = accounts.filter((a) => tokenHealth(a, now) === "expiring");
  const cs = language === "cs";

  // The most urgent first: a token Fio keeps refusing, one Zettl cannot read, a
  // refusal that may still be a Fio outage, one about to expire.
  let title: string;
  let description: string;
  let names: string;
  let showHelp = true;
  if (inactive.length) {
    title = cs ? "Fio token přestal platit" : "Fio stopped accepting the token";
    description = cs
      ? "Fio token odmítlo, nové platby se nenačítají u účtu:"
      : "Fio refused the token, so new payments are not loading for:";
    names = inactive.map((a) => a.name).join(", ");
  } else if (refresh.length) {
    title = t.syncErrorBannerTitle;
    description = t.syncErrorBannerDescription;
    names = refresh.map((a) => a.name).join(", ");
    showHelp = false;
  } else if (suspected.length) {
    title = cs ? "Fio neodpovědělo na token" : "Fio did not accept the token";
    description = cs
      ? "Fio token možná vypršel, nebo je Fio API dočasně nedostupné. Pokud to přetrvá, obnovte token. Účet:"
      : "The Fio token may have expired, or the Fio API is temporarily unavailable. If it persists, renew the token. Account:";
    names = suspected.map((a) => a.name).join(", ");
  } else if (expiring.length) {
    title = cs ? "Fio token brzy vyprší" : "A Fio token expires soon";
    description = cs
      ? "Bez automatického prodlužování přestane token brzy platit u účtu:"
      : "Without automatic renewal the token stops working soon for:";
    names = expiring.map((a) => a.name).join(", ");
  } else {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 rounded-xl border border-border bg-card shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-2">
      <div className="flex items-start gap-3 p-4">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-4 w-4 text-destructive" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-foreground">{title}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {description} <span className="font-medium text-foreground">{names}</span>
          </p>
          {showHelp && <FioTokenHelp />}
          <button
            onClick={() => navigate(`/${lang}/settings/integrations`)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <Settings className="h-3 w-3" />
            {t.syncErrorBannerAction}
          </button>
        </div>

        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
